import {useContext, useEffect, useState} from 'preact/hooks';

import {DEFAULT_APPEARANCE, loadAdminState, saveAppearance} from '../lib/api.js';
import {LanguageContext, useTranslate} from '../lib/i18n.js';

export default function AppearancePage() {
  const t = useTranslate();
  const {toggleLanguage} = useContext(LanguageContext);
  const [appearance, setAppearance] = useState(DEFAULT_APPEARANCE);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    loadAdminState()
      .then((data) => setAppearance(data.appearance))
      .catch((loadError) => setError(message(loadError)))
      .finally(() => setStatus('idle'));
  }, []);

  function change(key, value) {
    setAppearance((current) => ({...current, [key]: value}));
  }

  async function save() {
    setStatus('saving');
    setError('');
    try {
      setAppearance(await saveAppearance(appearance));
      shopify.toast.show(t('appearanceSaved'));
    } catch (saveError) {
      setError(message(saveError));
    } finally {
      setStatus('idle');
    }
  }

  return (
    <s-page heading={t('appearanceTitle')} inlineSize="small">
      <s-link slot="breadcrumb-actions" href="/">{t('back')}</s-link>
      <s-button slot="secondary-actions" onClick={toggleLanguage}>{t('language')}</s-button>
      <s-button slot="primary-action" variant="primary" loading={status === 'saving'} onClick={save}>{t('saveAppearance')}</s-button>

      {error && <s-banner tone="critical">{error}</s-banner>}
      {status === 'loading' && <s-section><s-paragraph>{t('loading')}</s-paragraph></s-section>}

      {status !== 'loading' && (
        <>
          <s-section heading={t('colors')}>
            <s-stack gap="base">
              <s-color-field label={t('selectedBorder')} value={appearance.selected_border_color} defaultValue={appearance.selected_border_color} onInput={(event) => change('selected_border_color', inputValue(event))} />
              <s-color-field label={t('normalBorder')} value={appearance.card_border_color} defaultValue={appearance.card_border_color} onInput={(event) => change('card_border_color', inputValue(event))} />
              <s-color-field label={t('background')} value={appearance.card_background} defaultValue={appearance.card_background} onInput={(event) => change('card_background', inputValue(event))} />
              <s-color-field label={t('textColor')} value={appearance.text_color} defaultValue={appearance.text_color} onInput={(event) => change('text_color', inputValue(event))} />
              <s-color-field label={t('mutedColor')} value={appearance.muted_text_color} defaultValue={appearance.muted_text_color} onInput={(event) => change('muted_text_color', inputValue(event))} />
            </s-stack>
          </s-section>
          <s-section heading={t('displayRules')}>
            <s-stack gap="base">
              <s-switch label={t('showBrand')} checked={appearance.show_brand} onChange={(event) => change('show_brand', checkedValue(event))} />
              <s-switch label={t('enableCollapse')} checked={appearance.enable_collapse} onChange={(event) => change('enable_collapse', checkedValue(event))} />
              <s-number-field label={t('collapsedCount')} min={1} max={10} value={String(appearance.collapsed_count)} onInput={(event) => change('collapsed_count', Number(inputValue(event)) || 2)} />
            </s-stack>
          </s-section>
        </>
      )}
    </s-page>
  );
}

function inputValue(event) {
  return event?.target && 'value' in event.target ? String(event.target.value) : '';
}

function checkedValue(event) {
  return Boolean(event?.target && 'checked' in event.target ? event.target.checked : false);
}

function message(error) {
  return error instanceof Error ? error.message : String(error);
}
